// Age from date of birth (2026-10-19) — the frontend mirror of backend/profile_age.py. Counted on the ISRAEL
// calendar date; a 29 February birth turns a year older on 1 March in a non-leap year. The backend is the
// authority (it stores the age computed from the date on every save and returns the effective age); this
// is for display and for the same pre-save check the backend applies.
export const AGE_RANGE_YEARS = [10, 100];   // = backend schemas.AGE_RANGE_YEARS

/** Today's date in Israel as 'YYYY-MM-DD'. `now` is for tests. */
export function israelTodayISO(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jerusalem', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function parseISO(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return { y, mo, d };
}

/** Completed years on `todayISO` (Israel today by default); null for no / bad / future date. */
export function ageFromDob(dobISO, todayISO = israelTodayISO()) {
  const b = parseISO(dobISO);
  const t = parseISO(todayISO);
  if (!b || !t) return null;
  if (b.y > t.y || (b.y === t.y && (b.mo > t.mo || (b.mo === t.mo && b.d > t.d)))) return null;
  let years = t.y - b.y;
  if (t.mo < b.mo || (t.mo === b.mo && t.d < b.d)) years -= 1;   // birthday not reached yet this year
  return years;
}

/** The age to show: from the date of birth when there is one, otherwise the stored legacy age. */
export function effectiveAge(dobISO, storedAge, todayISO) {
  const a = ageFromDob(dobISO, todayISO);
  if (a !== null) return a;
  const n = parseInt(storedAge, 10);
  return Number.isFinite(n) ? n : null;
}

/** Hebrew reason the date cannot be saved, or null — the same rule as backend profile_age.dob_problem. */
export function dobProblem(dobISO, todayISO = israelTodayISO()) {
  if (!parseISO(dobISO)) return 'תאריך לידה לא תקין.';
  if (dobISO > todayISO) return 'תאריך לידה לא יכול להיות בעתיד.';
  const a = ageFromDob(dobISO, todayISO);
  if (a === null || a < AGE_RANGE_YEARS[0] || a > AGE_RANGE_YEARS[1]) {
    return `תאריך הלידה מתאים לגיל ${a ?? '?'} — מחוץ לטווח הסביר (${AGE_RANGE_YEARS[0]}-${AGE_RANGE_YEARS[1]} שנים).`;
  }
  return null;
}

// ─── Three-field date of birth (יום / חודש / שנה), 2026-10-20 ───────────────────────────────────
// The API format stays 'YYYY-MM-DD'; the three fields are only how it is typed.

/** 'YYYY-MM-DD' -> { day: 'DD', month: 'MM', year: 'YYYY' }; anything else -> empty parts. */
export function isoToParts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? { day: m[3], month: m[2], year: m[1] } : { day: '', month: '', year: '' };
}

/** Does this day exist in this month of this year (29 February only in leap years)? */
export function isRealCalendarDate(y, m, d) {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return false;
  if (m < 1 || m > 12 || d < 1) return false;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];
  return d <= days;
}

/** "14/03/1968" (also "." or "-", 1-2 digit day / month) -> parts, or null when it is not a full date. */
export function parsePastedDob(text) {
  const m = /^\s*(\d{1,2})\s*[/.-]\s*(\d{1,2})\s*[/.-]\s*(\d{4})\s*$/.exec(String(text || ''));
  return m ? { day: m[1].padStart(2, '0'), month: m[2].padStart(2, '0'), year: m[3] } : null;
}

/** The state of the three fields:
 *   empty         — nothing typed (legacy users may leave it so)
 *   incomplete    — something typed, not yet a full day / month / 4-digit year
 *   invalid_date  — a full date that does not exist (31/04, 29/02 in a non-leap year)
 *   out_of_range  — a real date, but in the future or outside the existing 10-100 age range
 *   valid         — { iso: 'YYYY-MM-DD', age } on the Israel calendar date */
export function evaluateDobParts(parts, todayISO = israelTodayISO()) {
  const day = String((parts && parts.day) || '');
  const month = String((parts && parts.month) || '');
  const year = String((parts && parts.year) || '');
  if (!day && !month && !year) return { status: 'empty', iso: null, age: null };
  if (!/^\d{1,2}$/.test(day) || !/^\d{1,2}$/.test(month) || !/^\d{4}$/.test(year)) {
    return { status: 'incomplete', iso: null, age: null };
  }
  const [d, m, y] = [Number(day), Number(month), Number(year)];
  if (!isRealCalendarDate(y, m, d)) return { status: 'invalid_date', iso: null, age: null };
  const iso = `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  if (dobProblem(iso, todayISO)) return { status: 'out_of_range', iso: null, age: null };
  return { status: 'valid', iso, age: ageFromDob(iso, todayISO) };
}
